import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ChevronDownIcon } from "lucide-react";
import { FieldErrorMessage, LightText2 } from "../ui/Text";
import useClickOutside from "@/hooks/useClickOutSide";
import { Center } from "../css";
import { useFloating, flip, shift, offset, autoUpdate } from "@floating-ui/react-dom";

interface Option {
  label: string;
  value: string | number;
}

export interface SelectBoxProps {
  label: string;
  field: string;
  placeholder: string;
  options: Option[];
  disabled?: boolean;
  validation?: {
    required?: boolean;
  };
  onChange?: (value: string | number) => void;
  showSearch?: boolean;
  defaultValue?: string;
  isAddOtherEnabled?: boolean;
  formValueKey?: string;
  loading?: boolean;
  errorText?: string;
}

const SelectBox: React.FC<SelectBoxProps> = ({
  field,
  placeholder,
  options,
  disabled = false,
  validation,
  onChange,
  showSearch = false,
  defaultValue,
  isAddOtherEnabled = false,
  formValueKey,
  loading = false,
  errorText,
}) => {
  const {
    register,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { errors },
  } = useFormContext();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useClickOutside(() => setIsDropdownOpen(false));
  const selectedValue = watch(field);

  const errorMessage: any = errors[field]?.message;

  const { refs, floatingStyles }: any = useFloating({
    open: isDropdownOpen,
    placement: "bottom-start",
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const handleSelect = (option: Option) => {
    setValue(field, option.value, { shouldValidate: true, shouldDirty: true });
    setIsDropdownOpen(false);
    setSearchTerm("");
    if (onChange) {
      onChange(option.value);
    }
  };

  const getSelectedLabel = useCallback(() => {
    if (!options) return undefined;
    const selected = options.find((option) => option.value === selectedValue);

    if (isAddOtherEnabled && !selected) {
      return getValues(field);
    }

    return selected ? selected.label : placeholder;
  }, [options, selectedValue, isAddOtherEnabled, searchTerm, placeholder]);

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return options;
    const searchTermLower = searchTerm.toLowerCase();

    const categorizeOption = (option: any) => {
      const label = option.label.toLowerCase();

      // Exact matches
      if (label === searchTermLower) return "exactMatch";
      // Starts with matches
      if (label.startsWith(searchTermLower)) return "startsWith";
      // Contains matches
      if (label.includes(searchTermLower)) return "contains";
      return null;
    };
    const categorizedResults: any = {
      selected: [],
      exactMatch: [],
      startsWith: [],
      contains: [],
    };

    // First, handle selected value if exists
    if (selectedValue) {
      const selectedOption = options.find((option) => option.value === selectedValue);
      if (selectedOption) {
        categorizedResults.selected.push(selectedOption);
      }
    }

    // Categorize all matching options
    for (const option of options) {
      // Skip if it's the selected option (already handled)
      if (option.value === selectedValue) continue;

      const category = categorizeOption(option);
      if (category) {
        categorizedResults[category].push(option);
      }
    }
    return [...categorizedResults.selected, ...categorizedResults.exactMatch, ...categorizedResults.startsWith, ...categorizedResults.contains];
  }, [options, searchTerm, selectedValue]);

  useEffect(() => {
    if (defaultValue) {
      if (formValueKey) {
        const value = defaultValue[formValueKey as any];
        setValue(field, value, { shouldDirty: false });
      } else {
        setValue(field, defaultValue, { shouldDirty: false });
      }
      trigger(field);
    }
  }, [defaultValue, formValueKey]);

  useEffect(() => {
    if (inputRef.current && isDropdownOpen) {
      inputRef.current.focus();
    }
  }, [isDropdownOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <input
        type="hidden"
        {...register(field, {
          required: validation?.required ? "This field is required." : false,
        })}
      />

      <div
        className={`w-full px-3 py-7 text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500 cursor-pointer flex justify-between items-center ${
          errors[field]?.message ? "border-text-error" : ""
        } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
        ref={refs.setReference}
      >
        {showSearch ? (
          <input
            type="text"
            ref={inputRef}
            value={searchTerm}
            onChange={(e) => {
              let value = e.target.value;
              if (value?.trim().length === 0) {
                value = value.trim();
              }
              if (value.length <= 50) {
                setSearchTerm(value);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
            onFocus={() => setIsDropdownOpen(true)}
            placeholder={isDropdownOpen ? "Search..." : getSelectedLabel()}
            className={`transition-all w-full duration-100 ease-in-out text-nowrap text-13 outline-none placeholder:text-text-default`}
          />
        ) : (
          <span>{getSelectedLabel()}</span>
        )}
        <ChevronDownIcon className={`w-5 h-5 transition-transform ${isDropdownOpen ? "transform rotate-180" : ""}`} />
      </div>
      {/* Dropdown Options */}
      {!loading && isDropdownOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto select-none"
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            width: refs.reference.current?.offsetWidth,
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`px-4 py-2 cursor-pointer hover:bg-primaryOpacity-10 font-normal text-13 text-text-default ${
                  selectedValue === option.value ? "bg-primaryOpacity-10 text-text-primary font-medium" : ""
                }`}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </div>
            ))
          ) : (
            <Center className="px-4 py-6">
              <LightText2 text={loading ? "please wait..." : "No options found"} />
            </Center>
          )}
        </div>
      )}

      {/* Error State */}
      {!loading && errorText && (
        <Center className="px-4 py-6">
          <LightText2 text={errorText} />
        </Center>
      )}

      {errorMessage && <FieldErrorMessage text={errorMessage} />}
    </div>
  );
};

export default SelectBox;
